import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import config from '../config';

const OfferCreate = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [generatedOfferUrl, setGeneratedOfferUrl] = useState(null);

  // Fetch offer details
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${config.apiUrl}/api/offers/${offerId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data && response.data.success) {
          const offerData = response.data.data;
          setOffer(offerData);
        } else {
          setError('Failed to fetch offer details');
        }
      } catch (err) {
        console.error('Error fetching offer:', err);
        setError('Failed to fetch offer details');
      } finally {
        setLoading(false);
      }
    };

    if (offerId) {
      fetchOffer();
    }
  }, [offerId, getToken]);

  const handlePreviewOffer = () => {
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
  };

  const handleGenerateOffer = async () => {
    try {
      setGenerating(true);
      const token = await getToken();
      
      const response = await axios.post(
        `${config.apiUrl}/api/offers/${offerId}/generate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.success) {
        setGeneratedOfferUrl(response.data.offerUrl);
        // Show success message
        alert(`Offer generated successfully! URL: ${response.data.offerUrl}`);
        // Navigate back to sales workflow offers tab
        navigate('/app/sales-workflow?tab=1'); // Tab 1 is the offers tab
      } else {
        throw new Error(response.data.message || 'Failed to generate offer');
      }
    } catch (err) {
      console.error('Error generating offer:', err);
      alert(`Error generating offer: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const calculateTotal = () => {
    if (!offer?.audiences) return 0;
    return offer.audiences.reduce((total, audience) => {
      const price = parseFloat(audience.price || 0);
      const discount = parseFloat(audience.discount || 0);
      const discountAmount = audience.discountType === '%' 
        ? (price * discount / 100) 
        : discount;
      return total + (price - discountAmount);
    }, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getOfferExpiryDate = () => {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 7);
    return formatDate(expiry);
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/sales-workflow')}
          sx={{ mt: 2 }}
        >
          Back to Sales Workflow
        </Button>
      </Container>
    );
  }

  if (!offer) {
    return (
      <Container maxWidth={false}>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Offer not found
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/sales-workflow')}
          sx={{ mt: 2 }}
        >
          Back to Sales Workflow
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/sales-workflow')}
            sx={{ mb: 2 }}
          >
            Back to Sales Workflow
          </Button>
          
          <Typography variant="h4" gutterBottom>
            Generate Offer #{offer.id}
          </Typography>
          
          <Typography variant="subtitle1" color="text.secondary">
            Create a professional offer document for {offer.businessName}
          </Typography>
        </Box>

        {/* Offer Details */}
        <Grid container spacing={3}>
          {/* Business Information */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  Business Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Business Name
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {offer.businessName}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Business Type
                    </Typography>
                    <Typography variant="body1">
                      {offer.businessType || 'N/A'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip 
                      label={offer.status || 'New'} 
                      color={offer.status === 'Accepted' ? 'success' : 'primary'}
                      size="small"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Offer Details */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon color="primary" />
                  Offer Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Offer Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(offer.offerDate)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Expiry Date
                    </Typography>
                    <Typography variant="body1">
                      {getOfferExpiryDate()}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Value
                    </Typography>
                    <Typography variant="h6" color="success.main" fontWeight={700}>
                      {formatCurrency(calculateTotal())}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sales Person
                    </Typography>
                    <Typography variant="body1">
                      {offer.salesPersonName || 'Unassigned'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Advertising Package Details */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Advertising Package Details
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {offer.audiences && offer.audiences.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>School/Organisation</TableCell>
                          <TableCell>Channel</TableCell>
                          <TableCell>Ad Type</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>Add-ons</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Discount</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {offer.audiences.map((audience, index) => {
                          const price = parseFloat(audience.price || 0);
                          const discount = parseFloat(audience.discount || 0);
                          const discountAmount = audience.discountType === '%' 
                            ? (price * discount / 100) 
                            : discount;
                          const total = price - discountAmount;
                          
                          return (
                            <TableRow key={index}>
                              <TableCell>{audience.organizationName || 'N/A'}</TableCell>
                              <TableCell>{audience.channel}</TableCell>
                              <TableCell>{audience.adType}</TableCell>
                              <TableCell>{formatDate(audience.startDate)}</TableCell>
                              <TableCell>{formatDate(audience.endDate)}</TableCell>
                              <TableCell>
                                {(() => {
                                  let addons;
                                  try {
                                    addons = typeof audience.addons === 'string' ? JSON.parse(audience.addons) : audience.addons;
                                  } catch (error) {
                                    addons = null;
                                  }
                                  
                                  if (!addons) return 'None';
                                  
                                  const addonsList = [];
                                  
                                  if (addons.topSpot) {
                                    addonsList.push('✨ Top Spot');
                                  }
                                  
                                  if (addons.gif) {
                                    addonsList.push('🎬 Gif');
                                  }
                                  
                                  return addonsList.length > 0 ? addonsList.join(', ') : 'None';
                                })()}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(price)}</TableCell>
                              <TableCell align="right">
                                {discount > 0 && (
                                  <>
                                    {audience.discountType === '%' ? `${discount}%` : formatCurrency(discount)}
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {formatCurrency(total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={8} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            Total Offer Value:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'success.main' }}>
                            {formatCurrency(calculateTotal())}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No advertising packages found for this offer
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={<StarIcon />}
            onClick={handleGenerateOffer}
            disabled={generating}
            size="large"
          >
            {generating ? 'Generating...' : 'Generate Offer'}
          </Button>
        </Box>

        {generatedOfferUrl && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Offer Generated Successfully!
            </Typography>
            <Typography variant="body2">
              Share this link with your client: <strong>{generatedOfferUrl}</strong>
            </Typography>
          </Alert>
        )}
      </Box>

    </Container>
  );
};

// OfferPreviewContent component for the clean offer layout
const OfferPreviewContent = ({ offer, calculateTotal, formatCurrency, formatDate, getOfferExpiryDate }) => {
  return (
    <Box sx={{ p: 4, bgcolor: '#f8f9fa', minHeight: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        color: 'white',
        p: 4,
        borderRadius: 2,
        mb: 4,
        textAlign: 'center'
      }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          Austnews
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9 }}>
          Advertising Proposal
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.8, fontStyle: 'italic', mt: 1 }}>
          for {offer.businessName}
        </Typography>
      </Box>

      {/* Content */}
      <Paper sx={{ p: 4, mb: 4 }}>
        {/* Offer Summary */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Advertising Proposal Summary
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Proposal Details
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Proposal Date:</Typography>
                  <Typography>{formatDate(offer.offerDate)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Valid Until:</Typography>
                  <Typography>{getOfferExpiryDate()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Proposal ID:</Typography>
                  <Typography>#{offer.id}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Investment Summary
              </Typography>
              <Box sx={{ 
                bgcolor: 'success.light', 
                color: 'success.contrastText', 
                p: 2, 
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {formatCurrency(calculateTotal())}
                </Typography>
                <Typography variant="body2">
                  Total Investment
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Advertising Package */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Your Advertising Package
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {offer.audiences && offer.audiences.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>School/Publication</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ad Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Campaign Period</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Investment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offer.audiences.map((audience, index) => {
                    const price = parseFloat(audience.price || 0);
                    const discount = parseFloat(audience.discount || 0);
                    const discountAmount = audience.discountType === '%' 
                      ? (price * discount / 100) 
                      : discount;
                    const total = price - discountAmount;
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {audience.organizationName || 'School/Publication'}
                          </Typography>
                        </TableCell>
                        <TableCell>{audience.channel}</TableCell>
                        <TableCell>{audience.adType}</TableCell>
                        <TableCell>
                          {formatDate(audience.startDate)} - {formatDate(audience.endDate)}
                        </TableCell>
                        <TableCell align="right">
                          <Stack alignItems="flex-end">
                            {discount > 0 && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {formatCurrency(price)}
                              </Typography>
                            )}
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {formatCurrency(total)}
                            </Typography>
                            {discount > 0 && (
                              <Typography variant="caption" color="error">
                                Save {audience.discountType === '%' ? `${discount}%` : formatCurrency(discount)}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No advertising packages configured</Alert>
          )}
        </Box>

        {/* Value Proposition */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Why Choose Austnews?
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Targeted Reach
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect directly with your local community through our established school networks and digital platforms.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Proven Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Our advertising solutions have helped hundreds of local businesses grow their customer base and increase revenue.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Full Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  From design to delivery, our team provides complete support to ensure your advertising campaign succeeds.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Information */}
        <Box sx={{ 
          bgcolor: 'grey.50', 
          p: 3, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="h6" gutterBottom color="primary">
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" paragraph>
            We're excited to help you reach your advertising goals. Contact us today to discuss this proposal or answer any questions you may have.
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon color="primary" />
              <Typography>advertising@austnews.com.au</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon color="primary" />
              <Typography>1300 287 863</Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default OfferCreate;